import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { viewportOnce, EASE } from "@/lib/motion";
import { SERVICES } from "@/data/content";

const ServiceRow = ({ service, reverse }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <div
      id={service.anchor}
      ref={ref}
      data-testid={`service-${service.id}`}
      className="scroll-mt-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center"
    >
      {/* Image */}
      <div className={`lg:col-span-7 ${reverse ? "lg:order-2 lg:col-start-6" : "lg:order-1"}`}>
        <div className="relative overflow-hidden aspect-[16/11] group">
          <motion.img
            src={service.image}
            alt={service.title}
            loading="lazy"
            decoding="async"
            style={{ y }}
            className="absolute inset-0 h-[116%] w-full object-cover -top-[8%] transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-forest/10 group-hover:bg-forest/0 transition-colors duration-700" aria-hidden="true" />
          <span className="absolute top-6 left-6 font-serif text-ivory text-5xl md:text-6xl leading-none drop-shadow-[0_2px_10px_rgba(47,79,70,0.5)]">
            {service.index}
          </span>
        </div>
      </div>

      {/* Text */}
      <div className={`lg:col-span-5 ${reverse ? "lg:order-1 lg:col-start-1" : "lg:order-2"}`}>
        <Reveal>
          <p className="kicker text-gold mb-5">{service.tagline}</p>
        </Reveal>
        <Reveal delay={0.06}>
          <h3 className="font-serif font-light text-charcoal text-4xl md:text-5xl leading-tight tracking-tight">
            {service.title}
          </h3>
        </Reveal>
        <Reveal delay={0.12}>
          <p className="mt-6 text-charcoal/70 leading-relaxed md:text-lg max-w-md">
            {service.description}
          </p>
        </Reveal>
        <Reveal delay={0.18}>
          <div className="mt-8 h-px w-16 bg-gold" />
        </Reveal>
      </div>
    </div>
  );
};

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-ivory py-24 md:py-36 scroll-mt-24"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10">
        <div className="max-w-3xl mb-20 md:mb-28">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 0.9, ease: EASE }}
            className="kicker text-forest mb-6"
          >
            Five ways to journey
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewportOnce}
            transition={{ duration: 1, ease: EASE, delay: 0.05 }}
            className="font-serif font-light text-charcoal text-4xl md:text-6xl leading-[1.05] tracking-tight"
          >
            Pathways shaped around the life you actually live.
          </motion.h2>
        </div>

        <div className="flex flex-col gap-24 md:gap-36">
          {SERVICES.map((s, i) => (
            <ServiceRow key={s.id} service={s} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
